import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Google, useAuth } from '@langmail/shared-ui'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/ui/logo'
import { isTestEnvironment } from '@/utils/environment'

import { StandaloneSignInForm } from '../components/standalone-signin-form'
import { StandaloneSignupForm } from '../components/standalone-signup-form'

type StandaloneMode = 'create' | 'signin'

const ERROR_MESSAGES: Record<string, { message: string; variant: 'destructive' | 'info' }> = {
  required_scopes_missing: {
    message:
      'We have carefully designed Langmail to require a minimum set of permissions needed to craft your full experience. Please sign in again and allow the requested access.',
    variant: 'destructive',
  },
  oauth_cancelled: {
    message: "No problem — you can connect your Gmail whenever you're ready.",
    variant: 'info',
  },
  oauth_failed: {
    message: 'Unable to reconnect your account. Please try again.',
    variant: 'destructive',
  },
}

export const Auth = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<{ message: string; variant: 'destructive' | 'info' } | null>(null)
  // Standalone (@langmail.me) path: collapsed until chosen, then a sign-in /
  // create toggle. Presented co-equally with "Continue with Google".
  const [standaloneMode, setStandaloneMode] = useState<StandaloneMode | null>(null)
  // A standalone sign-up establishes the session immediately, but the mailbox
  // provisions asynchronously. Suppress the generic "already logged in → land"
  // redirect while that flow owns navigation, so we don't land the user before
  // the mailbox is active (Decision 6) — the form navigates once it is.
  const [standaloneSubmitting, setStandaloneSubmitting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const { user, signInWithGoogle, signInWithTestAccount } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user && !standaloneSubmitting) {
      navigate('/')
    }
  }, [user, navigate, standaloneSubmitting])

  // The marketing site's trial button opens the create-account form directly.
  useEffect(() => {
    if (searchParams.get('mode') === 'create') {
      setStandaloneMode('create')
    }
  }, [searchParams])

  // Check for error / status in URL query parameters
  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam && ERROR_MESSAGES[errorParam]) {
      setError(ERROR_MESSAGES[errorParam])
      // Clear the error from URL after displaying
      setSearchParams((params) => {
        params.delete('error')
        return params
      })
      return
    }
    // Landed here after completing a password reset — confirm and invite sign-in.
    if (searchParams.get('reset') === 'success') {
      setError({ message: 'Your password has been reset. You can now sign in.', variant: 'info' })
      setSearchParams((params) => {
        params.delete('reset')
        return params
      })
    }
  }, [searchParams, setSearchParams])

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(null)

    try {
      const { error: signInError } = await signInWithGoogle()
      if (signInError) {
        setError({ message: 'Sign-in failed. Please try again.', variant: 'destructive' })
      }
    } catch {
      setError({
        message: 'An unexpected error occurred during Google sign-in. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleTestSignIn = async () => {
    setLoading(true)
    setError(null)

    try {
      const { error: signInError } = await signInWithTestAccount()
      if (signInError) {
        setError({ message: 'Sign-in failed. Please try again.', variant: 'destructive' })
      }
    } catch {
      setError({
        message: 'An unexpected error occurred during test sign-in. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-dvh w-dvw flex-col items-center justify-center bg-background">
      <div className="flex w-full max-w-[400px] flex-1 items-center px-10 text-center">
        <div className="w-full">
          {error ? (
            <div className="mb-6">
              <Alert variant={error.variant}>
                <AlertDescription>{error.message}</AlertDescription>
              </Alert>
            </div>
          ) : null}

          <Logo />

          <div className="mt-16">
            <p className="mb-3.5 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
              Agent-native email & calendar
            </p>
            <h1 className="mb-3 font-semibold text-2xl text-foreground">Email & calendar you own.</h1>
            <p className="text-base text-muted-foreground">
              Continue with Google, or create a standalone Langmail address.
            </p>

            <Button
              className="mt-8 w-full gap-3 rounded-lg border border-border bg-card px-8 py-4 text-[15px] text-foreground hover:border-primary hover:bg-accent"
              disabled={loading}
              onClick={handleGoogleSignIn}
            >
              <Google />
              Continue with Google
            </Button>

            {standaloneMode === null ? (
              <Button
                className="mt-4 w-full gap-2 rounded-lg border border-border bg-transparent px-8 py-4 text-[15px] text-foreground hover:border-primary hover:bg-accent"
                disabled={loading}
                onClick={() => setStandaloneMode('create')}
              >
                Create a Langmail address
              </Button>
            ) : (
              <div className="mt-6">
                {/* Sign-in / create toggle within the standalone path. */}
                <div className="flex justify-center gap-6 font-mono text-sm">
                  <button
                    className={
                      standaloneMode === 'create'
                        ? 'text-foreground underline'
                        : 'text-muted-foreground hover:text-foreground'
                    }
                    onClick={() => setStandaloneMode('create')}
                    type="button"
                  >
                    Create account
                  </button>
                  <button
                    className={
                      standaloneMode === 'signin'
                        ? 'text-foreground underline'
                        : 'text-muted-foreground hover:text-foreground'
                    }
                    onClick={() => setStandaloneMode('signin')}
                    type="button"
                  >
                    Sign in
                  </button>
                </div>

                {standaloneMode === 'create' ? (
                  <StandaloneSignupForm
                    onSubmitEnd={() => setStandaloneSubmitting(false)}
                    onSubmitStart={() => setStandaloneSubmitting(true)}
                  />
                ) : (
                  <StandaloneSignInForm />
                )}
              </div>
            )}

            {isTestEnvironment() && (
              <Button
                className="mt-4 w-full gap-2 rounded-lg border border-border bg-transparent px-6 py-3 text-[15px] text-muted-foreground hover:bg-accent"
                data-testid="test-signin-button"
                disabled={loading}
                onClick={handleTestSignIn}
              >
                🧪 Test Sign In (Dev Only)
              </Button>
            )}

            <p className="mt-6 text-muted-foreground text-sm">Your data stays in Europe. Never training data.</p>
          </div>
        </div>
      </div>

      <div className="pb-6 text-center">
        <p className="text-muted-foreground text-xs">
          By continuing, you agree to our{' '}
          <a className="underline" href="https://langmail.me/terms" rel="noopener noreferrer" target="_blank">
            Terms
          </a>{' '}
          and{' '}
          <a className="underline" href="https://langmail.me/privacy" rel="noopener noreferrer" target="_blank">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  )
}
